import moment from 'moment';

const ga1ProductTypeList = [
  'donor',
  'HBF_DU',
  'HBF_DONOR',
  'service',
  'HBF_SU',
  'HBF_SERVICE',
  'HORN_WIDE_SU',
  'HORN_NARROW_SU',
  'HORN_WIDE_SERVICE',
  'HORN_NARROW_SERVICE'
];
const ga2ProductTypeList = ['ga2-n257', 'ga2-n258', 'ga2-n260'];

const productTypeMapping = {
  ES3_28: 'ES-3 28 (Boost)',
  'ES3-28': 'ES-3 28 (Boost)',
  ES4_39: 'ES-4 39',
  'ES4-39': 'ES-4 39',
  ESX_M5: 'ES-X M5',
  ESX_M6: 'ES-X M6',
  'ESX-n261': 'ES-X 28',
  ESX_n261: 'ES-X 28'
};

const DEVICE_GA_VERSION = {
  ga1: 'GA1',
  ga2: 'GA2',
}

const isDeviceOnline = (device) => device?.connectionState?.toLowerCase() === 'connected';

const isBundleOnline = (bundle) => {
  if (!bundle || !bundle?.members) return false;
  return bundle?.members?.every((member) => member?.connectionState?.toLowerCase() === 'connected');
};

const isDeviceLocked = (device) => ['lock', 'locked'].includes(device?.properties?.reported?.adminState?.toLowerCase());

const isDeviceWiFiEnabled = (device) => device?.properties?.reported?.wifi?.status?.toLowerCase() === 'enabled';

const isItBundle = (device) => {
  return device && device.bundleId && device.type && device.name && device.members && device.members.length > 0
    ? true
    : false;
};

const getModelFromProductType = (productType) => {
  return productTypeMapping[productType] || 'Unknown';
};

const getGenFromProductType = (productType) => {
  if (!productType) return 'Unknown';

  if (ga1ProductTypeList.some((type) => type.toUpperCase() === productType.toUpperCase())) return DEVICE_GA_VERSION.ga1;

  if (ga2ProductTypeList.some((type) => type.toUpperCase() === productType.toUpperCase())) return DEVICE_GA_VERSION.ga2;

  return 'Unknown';
};

const isGa2Device = (device) => getGenFromProductType(device?.properties?.reported?.productType) === 'GA2';

const getDeviceGaVersion = (device) => getGenFromProductType(device?.properties?.reported?.productType);

const extractRogueSUsFromDU = (du) => {
  if (!du) return [];
  let rogueSus = [];
  Object.entries(du).forEach(([key, value]) => {
    if (key.includes('rogue-su')) {
      rogueSus = [...rogueSus, ...value];
    }
  });

  const validRogueSus = rogueSus.filter((su) => su?.['serial-id']?.length >= 20);
  return validRogueSus.map((su) => ({
    productType: 'SERVICE',
    id: su['serial-id'],
    type: 'SU',
    ...su
  }));
};

/**
 * Extract DU and SU(s) from a GA2 device twin
 * @param {object} device
 * @returns an array of DU and SU objects
 * TODO: may need to add more necessary properties to each DU/SU in the later implementations
 */

const getGa2DeviceMembers = (device = {}, includeRogueSUs = true) => {
  if (!isGa2Device(device)) return [];

  const connectionState = device?.connectionState;
  const metadata = device?.properties?.reported?.$metadata;
  const reported = device?.properties?.reported || {};
  const rogueSUs = extractRogueSUsFromDU(reported?.DU || {});
  const normalSUs = Object.entries(reported)
    .filter(([key]) => key.includes('DU') || key.includes('SU'))
    .flatMap(([type, obj]) => {
      const expectedSerialId = obj?.['provision-info']?.['expected-serial-id'];

      if (expectedSerialId?.length >= 20) {
        const productType = type.includes('SU') ? 'SERVICE' : 'DONOR';
        const rfPath = reported?.[`rf-path-${type?.slice(2)}`];
        const $metadata = {
          rfPath: metadata?.[`rf-path-${type?.slice(2)}`],
          ...(metadata?.[type] ?? {})
        };

        return {
          id: expectedSerialId,
          connectionState,
          donorId: device.deviceId,
          productType,
          type,
          rfPath,
          $metadata,
          ...obj
        };
      }
      return [];
    });

  if (includeRogueSUs) {
    return [...rogueSUs, ...normalSUs];
  } else {
    return normalSUs;
  }
};

const getDeviceLastActivityTime = (device) =>
  device?.lastActivityTime ? moment(device.lastActivityTime).format('MM/DD/YYYY h:mm a') : 'Unknown';

export {
  ga1ProductTypeList,
  ga2ProductTypeList,
  DEVICE_GA_VERSION,
  productTypeMapping,
  isItBundle,
  isDeviceOnline,
  isBundleOnline,
  isDeviceLocked,
  isDeviceWiFiEnabled,
  getModelFromProductType,
  getGenFromProductType,
  isGa2Device,
  getDeviceGaVersion,
  extractRogueSUsFromDU,
  getGa2DeviceMembers,
  getDeviceLastActivityTime
};
